import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getProductByID, getParticipantsForProduct } from '../api/product.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';
import { getInstitutionByID } from '../api/institution.js';

defineMailTemplate(MailTemplateType.PRODUKT_LOESCHUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Workshop gelöscht',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
  <p>Das Produkt <b>{{product-name}}</b> wurde aus der Institution <b>{{institution-name}}</b> entfernt. </p>
  
  <p> Somit ist Ihre Anmeldung auch gelöscht. Bei Fragen kontaktieren Sie bitte den Support. </p>
 
  <p>Wir freuen uns, wenn wir Sie in Zukunft in einer unserer Veranstaltungen begr&uuml;&szlig;en d&uuml;rfen und empfehlen Ihnen hierf&uuml;r unser Vortrags- und Workshop-Programm auf <a href="https://anmeldung.uni-frankfurt.de">anmeldung.uni-frankfurt.de.</a></p>
  <p>Viele Gr&uuml;&szlig;e</p>
 `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.PRODUKT_LOESCHUNG),
  attachments: [],
  displayName: 'Veranstaltungseintragung durch Admin',
  editorHint: () => institutionMailHint(MailTemplateType.PRODUKT_LOESCHUNG),
});

export async function notifyProductDeletion(
  productID: string,
  deletedProduct: NarrowedEntity<
    Product,
    {
      name: 1;
      _class: 1;
      slotConfig: { start: 1; end: 1 };
      appointments: { name: 1; start: 1; end: 1; location: 1 };
    }
  >,
) {
  // get institution id
  const product = await getProductByID(productID)({ _id: 1, name: 1, institutionID: 1 });
  if (!product) throw new Error('product not found');

  // Get institution with color definitions
  const institution = await getInstitutionByID(product.institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found');

  // gather all participations
  const participations = await getParticipantsForProduct(product._id);
  const users = participations.map(p => p.user);

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  // build email
  const mailTemplate = await getMailTemplateForInstitution(
    institution._id,
    MailTemplateType.PRODUKT_LOESCHUNG,
  )(InstitutionMailTemplate.allFields);

  console.log('Send delete mail', product.name, institution.name);
  for (const user of users) {
    const token = await generateLoginToken({
      userID: user._id,
      userClass: 'User',
      ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
    });

    const sendEmail = async () => {
      const { content: mailContent } = (await assembleInstitutionMail({
        template: mailTemplate,
        infos: {
          [MailTemplateVars.TITLE]: user.title ?? '',
          [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
          [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
          [MailTemplateVars.PRODUCT_NAME]: product.name,
          [MailTemplateVars.INSTITUTION_NAME]: institution.name,
          [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
          [MailTemplateVars.PRODUCT_CARD]: InstitutionMailTemplate.mapProductVar(deletedProduct),
          [MailTemplateVars.PRODUCT_CARD_EN]: InstitutionMailTemplate.mapProductVar(deletedProduct, 'en'),
        } as any,
      })) as { content: any };

      if (!mailContent) return;
      await sendInstitutionEmail({
        email: user.email,
        subject: mailTemplate.subject,
        content: mailContent,
        institutionID: institution._id,
      });
    };

    const notificationType = await getNotificationType(user, mailTemplate, institution._id);
    if (notificationType === 'whatsapp') {
      await sendWhatsappWithEmailFallback({
        institutionID: institution._id,
        user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
        mailTemplate,
        mailTemplateType: MailTemplateType.PRODUKT_LOESCHUNG,
        parameters: [
          InstitutionMailTemplate.mapFirstNameVar(user) || '-',
          InstitutionMailTemplate.mapLastNameVar(user) || '-',
          product.name || '-',
        ],
        sendEmail,
      });
    } else if (notificationType === 'email') {
      await sendEmail();
    }
  }
}
